<template>
  <BaseResizableLeftPane
    :is-resizable="false"
    storage-key="agent/sidebar:width"
    @resized="(w) => $emit('sidebar-resized', w)"
  >
    <div class="conversation-list">
      <BasePaneHeader :is-hideable="false" compact />
      <div class="conversation-list-header">
        <BaseButton kind="auxiliary" text="New chat" @click="$emit('new-chat')">
          <template #icon>
            <PlusIcon />
          </template>
        </BaseButton>
      </div>
      <div class="conversation-groups">
        <BaseSkeletonList v-if="isLoading" is-compact :size="8" />
        <template v-else>
          <div
            v-for="group in groupedConversations"
            :key="group.label"
            class="conversation-group"
          >
            <div class="group-label">{{ group.label }}</div>
            <div
              v-for="conversation in group.items"
              :key="conversation.id"
              class="conversation-item"
              :class="{ active: conversation.id === activeConversationId }"
              role="button"
              tabindex="0"
              @click="$emit('select', conversation.id)"
              @keydown.enter="$emit('select', conversation.id)"
            >
              <span class="conversation-title">{{
                conversation.title || "New Conversation"
              }}</span>
              <button
                class="delete-button"
                @click.stop="$emit('delete', conversation.id)"
              >
                ×
              </button>
            </div>
          </div>
          <div v-if="conversations.length === 0" class="conversations-empty">
            No conversation
          </div>
        </template>
      </div>
    </div>
  </BaseResizableLeftPane>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";

import type { AiConversationSummary } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BasePaneHeader from "@/components/base/BasePaneHeader.vue";
import BaseResizableLeftPane from "@/components/base/BaseResizableLeftPane.vue";
import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";

type ConversationGroup = {
  label: string;
  items: AiConversationSummary[];
};

export default defineComponent({
  name: "AgentConversationList",
  components: {
    BaseButton,
    BasePaneHeader,
    BaseResizableLeftPane,
    BaseSkeletonList,
    PlusIcon,
  },
  props: {
    conversations: {
      type: Array as PropType<AiConversationSummary[]>,
      required: true,
    },
    activeConversationId: {
      type: String as PropType<string | null>,
      default: null,
    },
    /** Conversations are still being fetched: show a skeleton list. */
    isLoading: { type: Boolean, default: false },
  },
  emits: ["select", "new-chat", "delete", "sidebar-resized"],
  setup(props) {
    const groupedConversations = computed<ConversationGroup[]>(() => {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const yesterdayStart = new Date(todayStart);
      yesterdayStart.setDate(yesterdayStart.getDate() - 1);
      const weekStart = new Date(todayStart);
      weekStart.setDate(weekStart.getDate() - 7);

      const groups: Record<string, AiConversationSummary[]> = {};
      const order = ["Today", "Yesterday", "This week", "Older"];

      const sorted = [...props.conversations].sort(
        (a, b) => b.touched_last.getTime() - a.touched_last.getTime()
      );
      for (const conversation of sorted) {
        const date = conversation.touched_last;
        let label: string;
        if (date >= todayStart) {
          label = "Today";
        } else if (date >= yesterdayStart) {
          label = "Yesterday";
        } else if (date >= weekStart) {
          label = "This week";
        } else {
          label = "Older";
        }
        if (!groups[label]) groups[label] = [];
        groups[label].push(conversation);
      }

      return order
        .filter((label) => groups[label]?.length > 0)
        .map((label) => ({ label, items: groups[label] }));
    });

    return { groupedConversations };
  },
});
</script>

<style scoped lang="scss">
.conversation-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .conversation-list-header {
    padding: 12px;
    flex-shrink: 0;

    :deep(.base-button) {
      width: 100%;
    }
  }

  .conversation-groups {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: 0 8px 8px;

    .conversation-group {
      display: flex;
      flex-direction: column;
      margin-bottom: 8px;
    }

    .group-label {
      padding: 4px 8px;
      @include themed() {
        color: t($grey-40);
      }
    }

    .conversation-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 8px;
      border-radius: $border-radius-m;
      gap: 4px;

      @include themed() {
        color: t($grey-60);

        &:hover {
          background-color: t($grey-05);

          .delete-button {
            opacity: 1;
          }
        }

        &.active {
          background-color: t($grey-10);
          color: t($primary);
        }
      }

      .conversation-title {
        @extend %font-body-regular;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 13px;
      }

      .delete-button {
        flex-shrink: 0;
        background: none;
        border: none;
        opacity: 0;
        padding: 0 2px;
        line-height: 1;
        font-size: 16px;
        border-radius: $border-radius-s;
        transition: opacity 0.15s;

        @include themed() {
          color: t($grey-40);

          &:hover {
            color: t($primary);
            background-color: t($grey-15);
          }
        }
      }
    }

    .conversations-empty {
      padding: 8px;
      @extend %font-body-regular;
      @include themed() {
        color: t($grey-40);
      }
    }
  }
}
</style>
